import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsHelper } from 'src/app/utils/echarts/echarts-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { BiVizDashboardUsersActivity } from '../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { MopaDevicesChartDocs } from './mopa-devices.chart-documentation';
import { ChartDeviceDailyRevenueDataLoaderFactory } from './data-loaders/chart-device-daily-revenue';
import { ChartDeviceDailyRevenueByCountryDataLoaderFactory } from './data-loaders/chart-device-daily-revenue-by-country';
import { ChartDeviceDailyUsersDataLoaderFactory } from './data-loaders/chart-device-daily-users';
import { ChartDeviceDailyUsersByDeviceModelDataLoaderFactory } from './data-loaders/chart-device-daily-users-by-device-model';
import { TableRevenueDataLoaderFactory } from './data-loaders/table-revenue';
import { TableRevenueCountryDataLoaderFactory } from './data-loaders/table-revenue-by-country';
import { TableSelectedDevicesDataLoaderFactory } from './data-loaders/table-selected-devices';
import { TableUsersDataLoaderFactory } from './data-loaders/table-users';

@Component({
    selector: 'app-mopa-devices',
    templateUrl: './mopa-devices.component.html',
    styleUrls: ['./mopa-devices.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaDevicesComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Users';
    override DASHBOARD_ID = 'mopa-users';

    private defaultGcpProjectId: GcpProjectId =
        currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.devices.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.firebase_app_store',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            ignoredContextColumnIds: [
                'GA_FIRST_DEVICE_CATEGORY',
                'GA_FIRST_DEVICE_MODEL',
                'GA_FIRST_DEVICE_BRAND',
                'DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL',
            ],
        }).create(),
        'mopa.devices.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.devices.filter.firebase_country_european_union',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                ignoredContextColumnIds: [
                    'GA_FIRST_DEVICE_CATEGORY',
                    'GA_FIRST_DEVICE_MODEL',
                    'GA_FIRST_DEVICE_BRAND',
                    'DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL',
                ],
            }).create(),
        'mopa.devices.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.firebase_country_tier',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
            ignoredContextColumnIds: [
                'GA_FIRST_DEVICE_CATEGORY',
                'GA_FIRST_DEVICE_MODEL',
                'GA_FIRST_DEVICE_BRAND',
                'DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL',
            ],
        }).create(),
        'mopa.devices.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.firebase_country',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
            ignoredContextColumnIds: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
        }).create(),
        'mopa.devices.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.etl_payer',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            ignoredContextColumnIds: [
                'GA_FIRST_DEVICE_CATEGORY',
                'GA_FIRST_DEVICE_MODEL',
                'GA_FIRST_DEVICE_BRAND',
                'DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL',
            ],
        }).create(),
        'mopa.devices.filter.ga_first_device_category': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.ga_first_device_category',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'GA_FIRST_DEVICE_CATEGORY',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            ignoredContextColumnIds: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
        }).create(),
        'mopa.devices.filter.ga_first_device_brand': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.ga_first_device_brand',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'GA_FIRST_DEVICE_BRAND',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            filterContext: false,
            // ignoredContextColumnIds: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
        }).create(),
        'mopa.devices.filter.ga_first_device_model': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.devices.filter.ga_first_device_model',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'GA_FIRST_DEVICE_MODEL',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            ignoredContextColumnIds: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.devices.modify_time_users_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.devices.modify_time_users_datasource',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BiVizDashboardUsersActivity.sqlMap.dataset,
            tableId: BiVizDashboardUsersActivity.sqlMap.tableId,
        }).create(),

        'mopa.devices.revenue': new TableRevenueDataLoaderFactory({
            createdInstanceId: 'mopa.devices.revenue',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            filterContext: true,
            ignoredContextColumnIds: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
        }).create(),
        'mopa.devices.users': new TableUsersDataLoaderFactory({
            createdInstanceId: 'mopa.devices.users',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            filterContext: true,
            ignoredContextColumnIds: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
        }).create(),
        'mopa.devices.selected_devices':
            new TableSelectedDevicesDataLoaderFactory({
                createdInstanceId: 'mopa.devices.selected_devices',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                // filterContext: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
            }).create(),
        'mopa.devices.selected_devices_revenue_country':
            new TableRevenueCountryDataLoaderFactory({
                createdInstanceId:
                    'mopa.devices.selected_devices_revenue_country',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                // filterContext: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
            }).create(),
        'mopa.devices.selected_devices_daily_revenue':
            new ChartDeviceDailyRevenueDataLoaderFactory({
                createdInstanceId:
                    'mopa.devices.selected_devices_daily_revenue',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                // filterContext: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
            }).create(),
        'mopa.devices.selected_devices_daily_users':
            new ChartDeviceDailyUsersDataLoaderFactory({
                createdInstanceId: 'mopa.devices.selected_devices_daily_users',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                // filterContext: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
            }).create(),
        'mopa.devices.selected_devices_daily_revenue_by_country':
            new ChartDeviceDailyRevenueByCountryDataLoaderFactory({
                createdInstanceId:
                    'mopa.devices.selected_devices_daily_revenue_by_country',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                topN: 20,
                // filterContext: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
            }).create(),
        'mopa.devices.selected_devices_daily_users_by_device_model':
            new ChartDeviceDailyUsersByDeviceModelDataLoaderFactory({
                createdInstanceId:
                    'mopa.devices.selected_devices_daily_users_by_device_model',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                // filterContext: ['DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL'],
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    END OF DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.devices.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.devices.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.devices.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.devices.filter.firebase_app_store',
                datasourceColumnId: 'FIREBASE_APP_STORE',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.devices.filter.firebase_app_store']
                    .emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.devices.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.devices.filter.firebase_country_european_union',
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.devices.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.devices.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.devices.filter.firebase_country_tier',
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.devices.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.devices.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.devices.filter.firebase_country',
                datasourceColumnId: 'FIREBASE_COUNTRY',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.devices.filter.firebase_country']
                    .emitter,
        },
        ETL_PAYER: {
            id: 'mopa.devices.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.devices.filter.etl_payer',
                datasourceColumnId: 'ETL_PAYER',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.devices.filter.etl_payer'].emitter,
        },
        GA_FIRST_DEVICE_CATEGORY: {
            id: 'mopa.devices.filter.ga_first_device_category',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.devices.filter.ga_first_device_category',
                datasourceColumnId: 'GA_FIRST_DEVICE_CATEGORY',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.devices.filter.ga_first_device_category'
                ].emitter,
        },
        GA_FIRST_DEVICE_BRAND: {
            id: 'mopa.devices.filter.ga_first_device_brand',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.devices.filter.ga_first_device_brand',
                datasourceColumnId: 'GA_FIRST_DEVICE_BRAND',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.devices.filter.ga_first_device_brand'
                ].emitter,
        },
        GA_FIRST_DEVICE_MODEL: {
            id: 'mopa.devices.filter.ga_first_device_model',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.devices.filter.ga_first_device_model',
                datasourceColumnId: 'GA_FIRST_DEVICE_MODEL',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.devices.filter.ga_first_device_model'
                ].emitter,
        },
        DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL: {
            id: 'mopa.devices.filter.devices_detailed_ga_first_device_model',
            dashboardItemFactory: DashboardItemFactories['filter.string_input'](
                {
                    createdInstanceId:
                        'mopa.devices.filter.devices_detailed_ga_first_device_model',
                    datasourceColumnId:
                        'DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL',
                }
            ),
            // empty emitter StringInputFilter doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
    };

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.devices.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.devices.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.devices.modify_time_users_datasource']
                .emitter,
    };
    revenue = {
        id: 'lnk.mopa.devices.revenue',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: 'Devices',
            instanceId: 'mopa.devices.revenue',
            dashboardItem: {
                title: 'Top 200 devices by Revenue',
                viewMode: 'paged',
                chartDocumentation: MopaDevicesChartDocs.revenue,
            },
            columnsGetter: MopaDevicesComponent.tableRevenueColumnsGetter,
            nzPageSize: 10,
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.devices.revenue'].emitter,
    };
    users = {
        id: 'lnk.mopa.devices.users',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: 'Devices',
            instanceId: 'mopa.devices.users',
            dashboardItem: {
                title: 'Top 200 devices by Users',
                viewMode: 'paged',
                chartDocumentation: MopaDevicesChartDocs.users,
            },
            columnsGetter: MopaDevicesComponent.tableUsersColumnsGetter,
            nzPageSize: 10,
        }),
        dataLoaderEmitter: this.chartDataLoaders['mopa.devices.users'].emitter,
    };

    selected_devices = {
        id: 'lnk.mopa.devices.selected_devices',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: 'Devices',
            instanceId: 'mopa.devices.selected_devices',
            dashboardItem: {
                title: 'Selected devices',
                viewMode: 'paged',
                chartDocumentation: MopaDevicesChartDocs.selected_devices,
            },
            columnsGetter:
                MopaDevicesComponent.tableSelectedDevicesColumnsGetter,
            nzPageSize: 10,
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.devices.selected_devices'].emitter,
    };

    device_revenue_country = {
        id: 'lnk.mopa.devices.revenue_country',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: 'Devices',
            instanceId: 'mopa.devices.revenue_country',
            dashboardItem: {
                title: 'Selected devices revenue by country',
                viewMode: 'paged',
                chartDocumentation: MopaDevicesChartDocs.device_revenue_country,
            },
            columnsGetter:
                MopaDevicesComponent.tableDeviceRevenueCountryColumnsGetter,
            nzPageSize: 10,
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.devices.selected_devices_revenue_country'
            ].emitter,
    };

    selected_device_daily_revenue = {
        id: 'lnk.mopa.devices.device_daily_revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Devices',
            createdInstanceId: 'mopa.devices.chart.device_daily_revenue',
            dashboardItem: {
                title: 'Selected devices daily revenue',
                chartDocumentation:
                    MopaDevicesChartDocs.selected_device_daily_revenue,
            },
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v: any) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.devices.selected_devices_daily_revenue']
                .emitter,
    };

    selected_device_daily_revenue_by_country = {
        id: 'lnk.mopa.devices.selected_device_daily_revenue_by_country',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Devices',
            createdInstanceId:
                'mopa.devices.chart.selected_device_daily_revenue_by_country',
            dashboardItem: {
                title: 'Selected devices daily revenue by country',
                chartDocumentation:
                    MopaDevicesChartDocs.selected_device_daily_revenue_by_country,
            },
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v: any) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.devices.selected_devices_daily_revenue_by_country'
            ].emitter,
    };

    selected_device_daily_users = {
        id: 'lnk.mopa.devices.device_daily_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Devices',
            createdInstanceId: 'mopa.devices.chart.device_daily_users',
            dashboardItem: {
                title: 'Selected devices daily users',
                chartDocumentation:
                    MopaDevicesChartDocs.selected_device_daily_users,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'users',
            metricName: 'Users',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.devices.selected_devices_daily_users']
                .emitter,
    };

    selected_devices_daily_users_by_device_model = {
        id: 'lnk.mopa.devices.selected_devices_daily_users_by_device_model',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Devices',
            createdInstanceId:
                'mopa.devices.chart.selected_devices_daily_users_by_device_model',
            dashboardItem: {
                title: 'Selected devices daily users by device model',
                chartDocumentation:
                    MopaDevicesChartDocs.selected_devices_daily_users_by_device_model,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'users',
            metricName: 'Users',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.devices.selected_devices_daily_users_by_device_model'
            ].emitter,
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaDevicesComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaDevicesComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 30 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const gaFirstDeviceModelIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'GA_FIRST_DEVICE_MODEL',
                state: {
                    value: {
                        options: [],
                        filterType: 'include',
                    },
                    isFilterApplied: false,
                    isSplitApplied: false,
                },
            };
        const gaFirstDeviceBrandIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'GA_FIRST_DEVICE_BRAND',
                state: {
                    value: {
                        options: [],
                        filterType: 'include',
                    },
                    isFilterApplied: false,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
            gaFirstDeviceBrandIC,
            gaFirstDeviceModelIC,
        ];

        return initialContext;
    }

    static tableRevenueColumnsGetter(
        context: IQueryContext
    ): TableColumnItem[] {
        return [
            {
                id: 'first_device_brand',
                name: 'Device brand',
                nzLeft: true,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_brand',
                    'string'
                ),
            },
            {
                id: 'first_device_model',
                name: 'Device model',
                nzLeft: true,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_model',
                    'string'
                ),
            },
            {
                id: 'revenue_net_pln',
                name: 'Revenue net pln',
                nzLeft: false,
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'revenue_net_pln',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
            },
        ];
    }

    static tableDeviceRevenueCountryColumnsGetter(
        context: IQueryContext
    ): TableColumnItem[] {
        return [
            {
                id: 'unified_country',
                name: 'Country',
                nzLeft: true,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'unified_country',
                    'string'
                ),
            },
            {
                id: 'revenue_net_pln',
                name: 'Revenue net pln',
                nzLeft: false,
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'revenue_net_pln',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
            },
        ];
    }

    static tableUsersColumnsGetter(context: IQueryContext): TableColumnItem[] {
        return [
            {
                id: 'first_device_brand',
                name: 'Device brand',
                nzLeft: true,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_brand',
                    'string'
                ),
            },
            {
                id: 'first_device_model',
                name: 'Device model',
                nzLeft: true,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_model',
                    'string'
                ),
            },
            {
                id: 'users',
                name: 'Users',
                nzLeft: false,
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'users',
                    'number'
                ),
            },
        ];
    }

    static tableSelectedDevicesColumnsGetter(
        context: IQueryContext
    ): TableColumnItem[] {
        return [
            {
                id: 'first_device_category',
                name: 'Device category',
                nzLeft: false,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_category',
                    'string'
                ),
                // TODO does not work
                nzWidth: '50px',
            },
            {
                id: 'first_device_brand',
                name: 'Device brand',
                nzLeft: false,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_brand',
                    'string'
                ),
                // TODO does not work
                nzWidth: '50px',
            },
            {
                id: 'first_device_model',
                name: 'Device model',
                nzLeft: false,
                nzAlign: 'left',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'first_device_model',
                    'string'
                ),
                // TODO does not work
                nzWidth: '50px',
            },
            {
                id: 'users',
                name: 'Users',
                nzLeft: false,
                // nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'users',
                    'number'
                ),
            },
            {
                id: 'revenue_net_pln',
                name: 'Revenue net pln',
                nzLeft: false,
                // nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'revenue_net_pln',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
            },
        ];
    }
}
